import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useBrand } from "../context/ThemeContext";
import { SearchBar } from "./SearchBar";

// Full nav: stickers routes keep red theme, signs routes switch to blue theme
const NAV = [
  { to: "/stickers", label: "Stickers", group: "stickers" },
  { to: "/corflute", label: "Corflute", group: "signs" },
  { to: "/flags", label: "Flags", group: "signs" },
  { to: "/coasters", label: "Coasters", group: "signs" },
];
const INFO_NAV = [
  { to: "/about", label: "About Us", group: "info" },
];

function Logo({ theme }) {
  const src = theme === "signs"
    ? "/brand/kwik-signs.png"
    : theme === "kopy"
    ? "/brand/kwik-kopy.png"
    : "/brand/kwik-stickers.png";
  const alt = theme === "signs" ? "Kwik Signs" : theme === "kopy" ? "Kwik Kopy Shellharbour" : "Kwik Stickers";
  return (
    <img src={src} alt={alt} className="h-8 sm:h-9 w-auto" data-testid={`brand-logo-${theme}`} />
  );
}

export default function Header() {
  const { totals } = useCart();
  const { theme } = useBrand();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-lg bg-[#FAF8F5]/90 border-b border-[#E2DDD5]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-home-link">
          <Logo theme={theme} />
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {NAV.map((n) => (
            <NavItem key={n.to} n={n} />
          ))}
          <div className="w-56 lg:w-72 mx-2"><SearchBar /></div>
          {INFO_NAV.map((n) => (
            <NavItem key={n.to} n={n} />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/cart" className="relative btn-ghost" data-testid="header-cart-link">
            <ShoppingBag size={20} />
            <span className="hidden sm:inline">Cart</span>
            {totals.count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-1 -right-1 text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center" style={{ background: "var(--brand)" }}>
                {totals.count}
              </span>
            )}
          </Link>
          <button
            className="md:hidden btn-ghost"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div className="md:hidden px-5 pb-3"><SearchBar testid="mobile-search" onNavigate={() => setOpen(false)} /></div>

      {open && (
        <div className="md:hidden border-t border-[#E2DDD5] bg-[#FAF8F5]" data-testid="mobile-menu">
          <div className="px-5 py-3 flex flex-col gap-1">
            {[...NAV, ...INFO_NAV].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                className={`py-3 px-3 rounded-xl font-semibold ${loc.pathname === n.to ? "bg-[#F3EFEA]" : ""}`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ n }) {
  return (
    <NavLink
      to={n.to}
      data-testid={`nav-${n.to.replace("/", "")}`}
      className={({ isActive }) =>
        `btn-ghost text-[13px] whitespace-nowrap ${isActive ? "bg-[#F3EFEA]" : ""} ${n.group === "signs" ? "data-signs" : ""}`
      }
    >
      {n.label}
    </NavLink>
  );
}
